import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Phone, Video, Shield, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import { PRACTITIONER, IMAGES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    consent: false,
  });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Merci de confirmer le consentement RGPD.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Votre message a bien été envoyé. Réponse sous 48h.");
      setForm({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
        consent: false,
      });
    } catch (err) {
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact" className="page-enter pt-32">
      <SEO
        title="Contact & rendez-vous — Psychologue Paris 16e"
        description="Prendre rendez-vous avec François-Xavier Villette : Doctolib, formulaire sécurisé, téléphone, rappel. Cabinet Paris 16e et visioconsultation."
      />
      <section className="container-fxv py-16 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="overline gold-line">Contact & rendez-vous</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-serif text-5xl md:text-6xl text-[#0C2340] mt-6 leading-[1.05]">
              Prenons le temps d'un premier échange.
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-[#4A5568] mt-6 leading-relaxed text-lg">
              Vous pouvez choisir la modalité qui vous convient : Doctolib,
              formulaire sécurisé, téléphone ou demande de rappel. Vous serez
              accueilli dans un cadre confidentiel et bienveillant.
            </p>
          </Reveal>

          <div className="mt-10 space-y-5">
            <a
              href={PRACTITIONER.doctolibUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-doctolib"
              className="flex items-center justify-between p-5 border border-[#E2E8F0] hover:border-[#0C2340] transition-colors group"
            >
              <div>
                <p className="font-serif text-xl text-[#0C2340]">Réserver sur Doctolib</p>
                <p className="text-sm text-[#4A5568] mt-1">Choisir un créneau en ligne</p>
              </div>
              <ArrowRight
                size={20}
                strokeWidth={1.5}
                className="text-[#C5A059] group-hover:translate-x-1 transition-transform"
              />
            </a>
            <a
              href={`tel:${PRACTITIONER.phone}`}
              data-testid="contact-call"
              className="flex items-center justify-between p-5 border border-[#E2E8F0] hover:border-[#0C2340] transition-colors group"
            >
              <div>
                <p className="font-serif text-xl text-[#0C2340] flex items-center gap-2">
                  <Phone size={18} strokeWidth={1.5} />
                  {PRACTITIONER.phoneDisplay}
                </p>
                <p className="text-sm text-[#4A5568] mt-1">Appel direct</p>
              </div>
              <ArrowRight
                size={20}
                strokeWidth={1.5}
                className="text-[#C5A059] group-hover:translate-x-1 transition-transform"
              />
            </a>
            <a
              href={`mailto:${PRACTITIONER.email}`}
              data-testid="contact-email"
              className="flex items-center justify-between p-5 border border-[#E2E8F0] hover:border-[#0C2340] transition-colors group"
            >
              <div>
                <p className="font-serif text-xl text-[#0C2340] flex items-center gap-2">
                  <Mail size={18} strokeWidth={1.5} />
                  {PRACTITIONER.email}
                </p>
                <p className="text-sm text-[#4A5568] mt-1">Email — réponse sous 48h</p>
              </div>
              <ArrowRight
                size={20}
                strokeWidth={1.5}
                className="text-[#C5A059] group-hover:translate-x-1 transition-transform"
              />
            </a>
          </div>

          <div className="mt-10 p-6 bg-[#F0F4F8]">
            <p className="overline gold-line">Cabinet</p>
            <p className="mt-3 text-[#0C2340] flex items-start gap-3">
              <MapPin size={18} strokeWidth={1.5} className="text-[#C5A059] mt-1" />
              <span>{PRACTITIONER.address}</span>
            </p>
            <div className="mt-4 flex flex-wrap gap-4 text-xs text-[#4A5568]">
              <span className="inline-flex items-center gap-1.5">
                <Video size={12} strokeWidth={1.5} className="text-[#C5A059]" />
                Visioconsultation sécurisée
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Shield size={12} strokeWidth={1.5} className="text-[#C5A059]" />
                Secret professionnel
              </span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="bg-white border border-[#E2E8F0] p-8 md:p-10"
          >
            <p className="overline gold-line">Formulaire sécurisé</p>
            <h2 className="font-serif text-3xl text-[#0C2340] mt-4">
              Envoyer un message
            </h2>

            <div className="mt-8 grid sm:grid-cols-2 gap-5">
              <Field
                label="Nom"
                name="name"
                value={form.name}
                onChange={onChange}
                required
                testId="contact-name"
              />
              <Field
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                required
                testId="contact-email-input"
              />
              <Field
                label="Téléphone"
                name="phone"
                value={form.phone}
                onChange={onChange}
                testId="contact-phone"
              />
              <Field
                label="Sujet"
                name="subject"
                value={form.subject}
                onChange={onChange}
                testId="contact-subject"
              />
            </div>

            <div className="mt-5">
              <label className="block text-xs uppercase tracking-[0.18em] text-[#4A5568] mb-2">
                Message
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                required
                minLength={10}
                rows={6}
                data-testid="contact-message"
                className="w-full bg-[#FAFAFA] border border-[#E2E8F0] px-4 py-3 text-[#0C2340] focus:outline-none focus:border-[#0C2340]"
                placeholder="Décrivez brièvement votre demande…"
              />
            </div>

            <label className="mt-5 flex items-start gap-3 text-sm text-[#4A5568]">
              <input
                type="checkbox"
                name="consent"
                checked={form.consent}
                onChange={onChange}
                data-testid="contact-consent"
                className="mt-1 accent-[#0C2340]"
              />
              <span>
                J'accepte que les informations transmises soient utilisées
                uniquement dans le cadre de ma demande, conformément à la
                politique de confidentialité (RGPD).
              </span>
            </label>

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#0C2340] text-white px-7 py-3.5 text-sm font-medium hover:bg-[#163861] transition-colors disabled:opacity-60"
            >
              {loading ? "Envoi…" : "Envoyer le message"}
              <ArrowRight size={16} strokeWidth={1.5} />
            </button>
          </form>
        </div>
      </section>

      <section className="bg-[#0C2340] section-fxv">
        <div className="container-fxv">
          <p className="overline">
            <span className="gold-line">Accès au cabinet</span>
          </p>
          <h2 className="font-serif text-4xl md:text-5xl mt-6 text-white">
            Au cœur du 16e arrondissement de Paris
          </h2>
          <p className="text-[#C9D4E3] mt-4 max-w-2xl">
            Le cabinet est situé dans un quartier calme et facilement
            accessible en métro et en bus. L'adresse précise est communiquée
            lors de la confirmation du rendez-vous.
          </p>
          <div className="mt-10 grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8 aspect-video bg-[#1A3454] overflow-hidden">
              <iframe
                title="Localisation cabinet Paris 16e"
                src={PRACTITIONER.mapsEmbed}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
            <div className="lg:col-span-4">
              <img
                src={IMAGES.paris}
                alt="Architecture parisienne"
                className="w-full h-full object-cover aspect-video lg:aspect-auto"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required, testId }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-[#4A5568] mb-2">
        {label}
        {required && " *"}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testId}
        className="w-full bg-[#FAFAFA] border border-[#E2E8F0] px-4 py-3 text-[#0C2340] focus:outline-none focus:border-[#0C2340]"
      />
    </div>
  );
}
